<script setup>
    import NavigationLink from '@/Layouts/ClientNavigationLink';
    import { router } from '@inertiajs/vue3';
    import { currentCompany, setupFinished, stepIsCompleted, stepIsInProgress } from '@/Layouts/ClientUtils';
    import { ref } from 'vue';
    import ClientModal from '@/components/client/Modal';
    import PrimaryButton from '@/components/client/Buttons/PrimaryButton';
    import SecondaryButton from '@/components/client/Buttons/SecondaryButton';

    const props = defineProps({
        company: Object,
    });

    const isShowingLogoutLeaveModal = ref(false);
    const isShowingSwitchCompanyModal = ref(false);
    const isSwitchingCompany = ref(false);

    function closeLogoutLeaveModal() {
        isShowingLogoutLeaveModal.value = false;
    }

    function closeSwitchCompanyModal() {
        isShowingSwitchCompanyModal.value = false;
    }

    function switchCompany(company) {
        if (isSwitchingCompany.value) {
            return;
        }

        isSwitchingCompany.value = true;

        setTimeout(() => {
            router.visit(`/kund/${company.slug}`);
            isShowingSwitchCompanyModal.value = false;
            isSwitchingCompany.value = false;
        }, 300);
    }

    function logout () {
        if (confirm('Är du säker på att du vill logga ut?')) {
            router.visit(`/kund/${props.company.slug}/logga-ut`);
        }
    }

    function leaveClientArea() {
        window.location.href = '/';
    }
</script>

<script>
    import { directive } from 'vue-tippy';

    export default {
        directives: {
            tippy: directive,
        },
    }
</script>

<template>
    <client-modal :show="isShowingLogoutLeaveModal" @close="() => closeLogoutLeaveModal()">
        <div class="p-10 text-gray md:flex md:-mx-2">
            <secondary-button
                @click="leaveClientArea"
                class="w-full md:w-1/2 md:mx-2">Lämna Mina sidor Företag</secondary-button>

            <primary-button
                @click="logout"
                class="w-full mt-2 md:mt-0 md:w-1/2 md:mx-2">Logga ut</primary-button>
        </div>
    </client-modal>

    <client-modal :show="isShowingSwitchCompanyModal" @close="() => closeSwitchCompanyModal()">
        <div class="p-10 text-gray md:flex md:-mx-2">
            <table class="min-w-full divide-y divide-gray-300">
                <thead class="bg-gray-50">
                    <tr>
                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Företag</th>
                    </tr>
                </thead>

                <tbody class="divide-y divide-gray-200 bg-white">
                    <tr v-for="companyy in $page.props.companies">
                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ companyy.name }}</td>
                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 pull-right flex flex-row-reverse">
                            <primary-button
                                v-if="companyy.name !== currentCompany().name"
                                :loading="isSwitchingCompany"
                                @click="switchCompany(companyy)">Välj företag</primary-button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </client-modal>

    <div class="bg-[#f8fafc] text-gray min-h-screen">
        <div class="bg-[#a2d8f0] px-8 py-6 flex fixed w-full z-[900]">
            <img src="/assets/frontend/logo.png" style="width: 200px; transform: translateZ(0); image-rendering: -webkit-optimize-contrast;">

            <svg @click="isShowingLogoutLeaveModal = true" xmlns="http://www.w3.org/2000/svg" class="cursor-pointer ml-auto self-center text-red h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
        </div>

        <div class="hidden lg:block bg-blue min-h-screen w-52 flex-none fixed pt-[81.83px] overflow-auto">
            <div class="flex flex-col px-3 py-6 items-center justify-center">
                <div class="self-center text-[#5c6675] font-semibold tracking-wide text-center mt-2 break-word">
                    <h3 class="text-xl">{{ currentCompany().name }}</h3>
                    <span class="text-sm break-all">{{ currentCompany().user?.email }}</span>
                </div>
                <a
                    v-if="$page.props.companies.length > 1"
                    @click="isShowingSwitchCompanyModal = true" class="hover:cursor-pointer text-sm underline mt-2 block font-semibold font-averta">(Byt företag)</a>
                <a @click="logout" class="hover:cursor-pointer text-sm underline mt-2 block font-semibold font-averta">(Logga ut)</a>
            </div>

            <ul class="absolute overflow-auto w-full">
                <navigation-link
                    :href="`/kund/${$page.props.company.slug}`"
                    :setup-finished="setupFinished()"
                    :completed="stepIsCompleted('welcome')"
                    :in-progress="stepIsInProgress('welcome')"
                    :active="$page.component === 'Client/Index'">
                    <template #icon>
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 13v-1m4 1v-3m4 3V8M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                        </svg>
                    </template>

                    Startsida
                </navigation-link>

                <navigation-link
                    :href="`/kund/${$page.props.company.slug}/foretag`"
                    :setup-finished="setupFinished()"
                    :locked="!stepIsCompleted('welcome')"
                    :in-progress="stepIsInProgress('contact')"
                    :completed="stepIsCompleted('contact')"
                    v-tippy="stepIsCompleted('welcome') ? {} : {
                        content: 'Oops... Här är det låst till dess att du är klar med tidigare steg...',
                        placement: 'right'
                    }"
                    :active="$page.component === 'Client/Company'">
                    <template #icon>
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                        </svg>
                    </template>

                    Kontaktuppgifter
                </navigation-link>

                <navigation-link
                    :href="`/kund/${$page.props.company.slug}/foretags-sida`"
                    :setup-finished="setupFinished()"
                    :locked="!stepIsCompleted('contact')"
                    :in-progress="stepIsInProgress('page')"
                    :completed="stepIsCompleted('page')"
                    v-tippy="stepIsCompleted('contact') ? {} : {
                        content: 'Oops... Här är det låst till dess att du är klar med tidigare steg...',
                        placement: 'right'
                    }"
                    :active="$page.component === 'Client/Page/Page'">
                    <template #icon>
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                    </template>

                    Min företagssida
                </navigation-link>

                <navigation-link
                    :href="`/kund/${$page.props.company.slug}/kampanjer`"
                    :setup-finished="setupFinished()"
                    :locked="!stepIsCompleted('page')"
                    :in-progress="stepIsInProgress('campaigns')"
                    :completed="stepIsCompleted('campaigns')"
                    v-tippy="stepIsCompleted('page') ? {} : {
                        content: 'Oops... Här är det låst till dess att du är klar med tidigare steg...',
                        placement: 'right'
                    }"
                    :active="['Client/Campaigns', 'Client/EditCampaign', 'Client/NewCampaign'].includes($page.component)">
                    <template #icon>
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </template>

                    Kampanjer
                </navigation-link>

                <navigation-link
                    :href="`/kund/${$page.props.company.slug}/faq`"
                    :excludedFromInstallation="true"
                    :active="['Client/Faq'].includes($page.component)">
                    <template #icon>
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </template>

                    Frågor och svar
                </navigation-link>
            </ul>
        </div>
        <div class="flex font-averta antialiased">
            <div class="w-full lg:pl-44">
                <div class="p-6 lg:p-24 pt-[110px]">
                    <slot/>
                </div>

                <div class="md:hidden sticky bottom-0 bg-blue w-full z-[1000]">
                    <ul class="list-none flex">
                        <navigation-link
                            :href="`/kund/${$page.props.company.slug}`"
                            :setup-finished="setupFinished()"
                            :completed="stepIsCompleted('welcome')"
                            :in-progress="stepIsInProgress('welcome')"
                            :active="$page.component === 'Client/Index'"
                            class="w-1/4">
                            <template #icon>
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 13v-1m4 1v-3m4 3V8M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                                </svg>
                            </template>

                            Startsida
                        </navigation-link>

                        <navigation-link
                            :href="`/kund/${$page.props.company.slug}/foretag`"
                            :setup-finished="setupFinished()"
                            :locked="!stepIsCompleted('welcome')"
                            :in-progress="stepIsInProgress('contact')"
                            :completed="stepIsCompleted('contact')"
                            v-tippy="stepIsCompleted('welcome') ? {} : {
                                content: 'Oops... Här är det låst till dess att du är klar med tidigare steg...',
                                placement: 'right'
                            }"
                            :active="$page.component === 'Client/Company'"
                            class="w-1/4">
                            <template #icon>
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                                </svg>
                            </template>

                            Kontakt
                        </navigation-link>

                        <navigation-link
                            :href="`/kund/${$page.props.company.slug}/foretags-sida`"
                            :setup-finished="setupFinished()"
                            :locked="!stepIsCompleted('contact')"
                            :in-progress="stepIsInProgress('page')"
                            :completed="stepIsCompleted('page')"
                            v-tippy="stepIsCompleted('contact') ? {} : {
                                content: 'Oops... Här är det låst till dess att du är klar med tidigare steg...',
                                placement: 'right'
                            }"
                            :active="$page.component === 'Client/Page/Page'"
                            class="w-1/4">
                            <template #icon>
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                            </template>

                            Företagssida
                        </navigation-link>

                        <navigation-link
                            :href="`/kund/${$page.props.company.slug}/kampanjer`"
                            :setup-finished="setupFinished()"
                            :locked="!stepIsCompleted('page')"
                            :in-progress="stepIsInProgress('campaigns')"
                            :completed="stepIsCompleted('campaigns')"
                            v-tippy="stepIsCompleted('page') ? {} : {
                                content: 'Oops... Här är det låst till dess att du är klar med tidigare steg...',
                                placement: 'right'
                            }"
                            :active="['Client/Campaigns', 'Client/EditCampaign', 'Client/NewCampaign'].includes($page.component)"
                            class="w-1/4">
                            <template #icon>
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </template>

                            Kampanjer
                        </navigation-link>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>
